/**
 * portalInviteService — the staff read side of `portalInvites`, plus revoke.
 *
 * Closes the gap left by slice P1 (2026-07-27, `74ca1e2`): the
 * `revokePortalInvite` callable shipped deployed and tested, and nothing in the
 * client ever called it. A mis-sent invite could only be revoked by hand with
 * the Admin SDK.
 *
 * WHY THIS NEEDS THREE QUERIES AND NOT ONE
 * ---------------------------------------------------------------------------
 * Invites do not all point at a student the same way, because they do not all
 * MEAN the same thing:
 *
 *   kind 'claim'    — binds the FIRST parent. Carries `studentIds: [...]`.
 *   kind 'student'  — a student's own account. Also carries `studentIds`.
 *   kind 'guardian' — adds a SECOND parent. Carries `studentIds: []` and binds
 *                     to `familyId`, because a guardian invite grants
 *                     FAMILY-WIDE read, not access to one child.
 *   (legacy)        — pre-P1 documents carry a singular `studentId`, no `kind`,
 *                     no `tokenHash` and no `expiresAt`. One exists in
 *                     production. It cannot be claimed through the token flow,
 *                     but staff still need to SEE it, or the panel silently
 *                     under-reports who was invited.
 *
 * A single query would therefore hide exactly the invite with the widest blast
 * radius — the guardian one. That is the opposite of what a revoke surface is
 * for, so all three shapes are fetched and merged.
 *
 * NO COMPOSITE INDEX IS NEEDED and that is deliberate: each query filters on a
 * single field (auto-indexed) and the sort happens in memory. Adding
 * `orderBy('sentAt')` to any of them would demand a composite index that the
 * emulator does not require — a prod-only 400 no test could catch.
 *
 * RULES ARE NOT FILTERS: `portalInvites` is `allow read: if isEmployeeOrAdmin()`
 * (firestore.rules) with all client writes denied. These reads carry no
 * per-document predicate to match, and the revoke goes through the callable,
 * which enforces `sendEmail` on the server. The UI hides the button for staff
 * without that permission so nobody is offered an action that will fail.
 */

import { db, functions } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export const PORTAL_INVITES = 'portalInvites';

/** Status values written by functions/portalClaims.js. */
export const INVITE_PENDING = 'pending';
export const INVITE_CLAIMED = 'claimed';
export const INVITE_REVOKED = 'revoked';

// Human labels for `kind`. A missing kind means a pre-P1 document; the server's
// own inviteKind() defaults the same way, so the two agree.
const KIND_LABELS = {
	claim: 'Account setup',
	guardian: 'Second parent',
	student: 'Student account',
};

export function inviteKindLabel(invite) {
	return KIND_LABELS[invite?.kind] || KIND_LABELS.claim;
}

/**
 * Firestore Timestamp | Date | null → Date | null.
 * Invites written before P1 omit `expiresAt` entirely, so every caller has to
 * cope with null rather than assuming a date is there.
 */
export function toDate(value) {
	if (!value) return null;
	if (typeof value.toDate === 'function') return value.toDate();
	if (value instanceof Date) return value;
	return null;
}

/**
 * Has this invite passed its expiry?
 *
 * Only meaningful for a PENDING invite — a claimed one is spent and a revoked
 * one is dead, whatever the clock says. An invite with no `expiresAt` is never
 * reported as expired: the honest answer for a legacy document is "unknown",
 * and showing "Expired" would assert something the data does not say.
 */
export function isExpired(invite, now = new Date()) {
	if (!invite || invite.status !== INVITE_PENDING) return false;
	const exp = toDate(invite.expiresAt);
	return exp ? exp.getTime() < now.getTime() : false;
}

/**
 * Every invite that concerns this student — their own, plus any family-wide
 * guardian invite. `familyId` is optional: 63 of 76 production students are MMS
 * imports with no family binding, and those simply have no guardian invites to
 * find.
 *
 * Deduped by document id, because a claim invite naming this student AND
 * carrying the family id would otherwise appear twice.
 */
export async function fetchInvitesForStudent({ studentId, familyId } = {}) {
	if (!studentId) return [];

	const col = collection(db, PORTAL_INVITES);
	const queries = [
		getDocs(query(col, where('studentIds', 'array-contains', studentId))),
		// Legacy singular field — see the header. Kept because the document is
		// real, in production, and invisible to the array-contains query.
		getDocs(query(col, where('studentId', '==', studentId))),
	];
	if (familyId) {
		queries.push(getDocs(query(col, where('familyId', '==', familyId))));
	}

	const snaps = await Promise.all(queries);
	const byId = new Map();
	snaps.forEach((snap) => {
		snap.docs.forEach((d) => {
			if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() });
		});
	});

	return sortInvites([...byId.values()]);
}

/**
 * Newest first, by whichever timestamp the document actually has.
 *
 * Pre-P1 invites carry `sentAt` but no `createdAt`, so reading only one field
 * would sort a real invite to the bottom as though it were undated. Ties and
 * missing dates keep a stable order rather than jumping between renders.
 */
export function sortInvites(invites) {
	return [...invites].sort((a, b) => {
		const at = toDate(a.sentAt) || toDate(a.createdAt);
		const bt = toDate(b.sentAt) || toDate(b.createdAt);
		if (at && bt) return bt.getTime() - at.getTime();
		if (at) return -1;
		if (bt) return 1;
		return String(a.id).localeCompare(String(b.id));
	});
}

/**
 * BULK read for the roster column.
 *
 * `fetchInvitesForStudent` above is the right shape for ONE student — three
 * narrow queries, no composite index — and the wrong shape for a table. The
 * roster is 87 students, so per-row it would fire ~261 queries to answer a
 * question the whole collection answers in one read. `portalInvites` is small
 * (36 documents in production; invites are per FAMILY, not per student) and
 * staff-only, so reading it whole and indexing in memory is cheaper, simpler,
 * and does not grow with roster size. Reads are this project's only real
 * Firestore cost constraint.
 */
export async function fetchAllInvites() {
	const snap = await getDocs(collection(db, PORTAL_INVITES));
	return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Invites indexed for per-row lookup, newest first inside each bucket.
 *
 * Two buckets, because invites bind two different ways (see the file header):
 *   byStudent — `studentIds: [...]`, plus the legacy singular `studentId`.
 *   byFamily  — GUARDIAN invites only.
 *
 * WHY GUARDIAN-ONLY IN byFamily, when a CLAIMED claim invite also carries a
 * familyId: a claim invite names the students it covers, so a sibling enrolled
 * after it was sent is genuinely not on it. Indexing claimed invites by family
 * would report that sibling as "Claimed" on the strength of an invite that
 * never mentioned them — hiding a student who still needs one. Under-reporting
 * sends someone to look; over-reporting sends nobody. The bias goes the safe
 * way deliberately.
 */
export function indexInvites(invites) {
	const byStudent = new Map();
	const byFamily = new Map();
	const push = (map, key, invite) => {
		if (!key) return;
		const list = map.get(key);
		if (list) list.push(invite);
		else map.set(key, [invite]);
	};

	(invites || []).forEach((invite) => {
		const ids = Array.isArray(invite.studentIds) ? invite.studentIds : [];
		ids.forEach((id) => push(byStudent, id, invite));
		// Legacy pre-P1 shape: singular field, no `kind`, no `expiresAt`.
		if (invite.studentId) push(byStudent, invite.studentId, invite);
		if (invite.kind === 'guardian') push(byFamily, invite.familyId, invite);
	});

	byStudent.forEach((list, key) => byStudent.set(key, sortInvites(list)));
	byFamily.forEach((list, key) => byFamily.set(key, sortInvites(list)));
	return { byStudent, byFamily };
}

/** Portal-access states, in the order the roster column sorts them. */
export const ACCESS_NONE = 'none';
export const ACCESS_EXPIRED = 'expired';
export const ACCESS_REVOKED = 'revoked';
export const ACCESS_INVITED = 'invited';
export const ACCESS_CLAIMED = 'claimed';

/**
 * `rank` exists so ONE click on the column header puts the work at the top:
 * ascending runs not-invited → expired → revoked → invited → claimed, i.e.
 * everything needing an action before everything already settled. Sorting on
 * the label instead would order them alphabetically, which is meaningless.
 */
const ACCESS_META = {
	[ACCESS_NONE]: { rank: 0, label: 'Not invited', variant: 'warning' },
	[ACCESS_EXPIRED]: { rank: 1, label: 'Expired', variant: 'danger' },
	[ACCESS_REVOKED]: { rank: 2, label: 'Revoked', variant: 'neutral' },
	[ACCESS_INVITED]: { rank: 3, label: 'Invited', variant: 'info' },
	[ACCESS_CLAIMED]: { rank: 4, label: 'Claimed', variant: 'success' },
};

/**
 * One student's portal-access state, across every invite that concerns them.
 *
 * Precedence, and each step is a claim about a real person's access:
 *   claimed  — someone signed in with it. Spent, and true regardless of dates.
 *   invited  — a pending invite that has NOT passed its expiry.
 *   expired  — pending, but the link in that family's inbox no longer works.
 *              Distinct from `invited` because the actions differ: one is
 *              waiting, the other needs a resend. Collapsing them would report
 *              a dead link as an outstanding invitation.
 *   revoked  — deliberately withdrawn; not a problem, not access either.
 *   none     — nothing was ever sent.
 *
 * `isExpired` (above) never calls a legacy invite expired, because those carry
 * no `expiresAt` and "Expired" would assert something the document does not
 * say. That reticence is inherited here on purpose.
 */
export function portalAccessFor({ studentId, familyId } = {}, index, now = new Date()) {
	const seen = new Map();
	const add = (list) => (list || []).forEach((i) => { if (!seen.has(i.id)) seen.set(i.id, i); });
	add(index?.byStudent?.get(studentId));
	if (familyId) add(index?.byFamily?.get(familyId));
	const invites = sortInvites([...seen.values()]);

	const pending = invites.filter((i) => i.status === INVITE_PENDING);
	const claimed = invites.find((i) => i.status === INVITE_CLAIMED);
	const live = pending.find((i) => !isExpired(i, now));
	const dead = pending.find((i) => isExpired(i, now));
	const revoked = invites.find((i) => i.status === INVITE_REVOKED);

	let state = ACCESS_NONE;
	let invite = null;
	if (claimed) { state = ACCESS_CLAIMED; invite = claimed; }
	else if (live) { state = ACCESS_INVITED; invite = live; }
	else if (dead) { state = ACCESS_EXPIRED; invite = dead; }
	else if (revoked) { state = ACCESS_REVOKED; invite = revoked; }

	return { state, invite, ...ACCESS_META[state] };
}

/**
 * The hover/title line for a roster cell. The BADGE already carries the answer
 * on its own — this only adds dates, never the state itself, because a
 * hover-only affordance is unusable on the installed PWA and hardest of all
 * for the portal's primary user to find.
 */
export function portalAccessDetail(access) {
	const invite = access?.invite;
	const on = (v) => { const d = toDate(v); return d ? d.toLocaleDateString() : null; };
	const to = invite?.recipientEmail ? ` to ${invite.recipientEmail}` : '';

	switch (access?.state) {
		case ACCESS_CLAIMED: {
			const when = on(invite.claimedAt);
			return when ? `Claimed ${when}${to}` : `Claimed${to}`;
		}
		case ACCESS_INVITED: {
			const sent = on(invite.sentAt) || on(invite.createdAt);
			const exp = on(invite.expiresAt);
			return [sent ? `Invited ${sent}` : 'Invited', exp ? `expires ${exp}` : null]
				.filter(Boolean).join(' · ') + to;
		}
		case ACCESS_EXPIRED: {
			const exp = on(invite.expiresAt);
			return (exp ? `Invite expired ${exp}` : 'Invite expired') + to + ' — needs resending';
		}
		case ACCESS_REVOKED: {
			const when = on(invite.revokedAt);
			return when ? `Revoked ${when}` : 'Revoked';
		}
		default:
			return 'No portal invite has been sent';
	}
}

/**
 * Revoke a pending invite. Server semantics, already tested in
 * `npm run test:guardian` and NOT re-implemented here:
 *   pending  → revoked
 *   revoked  → successful no-op (`alreadyRevoked: true`)
 *   claimed  → throws `failed-precondition`, because revoking cannot un-create
 *              an account and pretending otherwise would be a lie the UI tells
 *              about a real person's access.
 */
export const revokePortalInvite = async (inviteId) => {
	const res = await httpsCallable(functions, 'revokePortalInvite')({ inviteId });
	return res.data;
};
